<template>
  <div class="av-wrap">
    <!-- carregando -->
    <div v-if="loading" class="av-card av-card--state">
      <span class="av-muted">Carregando…</span>
    </div>

    <!-- já avaliada -->
    <div v-else-if="jaAvaliada" class="av-card">
      <div class="avatar avatar--xl avatar--ring">{{ doctorInitials }}</div>
      <h1 class="av-title">Você já avaliou esta consulta</h1>
      <div
        class="av-stars av-stars--readonly"
        :aria-label="`Nota ${enviada.stars} de 5`"
      >
        <span
          v-for="n in 5"
          :key="n"
          class="av-star"
          :class="{ 'av-star--on': n <= enviada.stars }"
          aria-hidden="true"
        >
          ★
        </span>
      </div>
      <div v-if="enviada.tags && enviada.tags.length" class="av-tags">
        <span v-for="t in enviada.tags" :key="t" class="av-tag av-tag--on">
          {{ tagLabel(t) }}
        </span>
      </div>
      <p v-if="enviada.description" class="av-quote">
        "{{ enviada.description }}"
      </p>
      <button class="btn btn--outline btn--block" @click="voltarInicio">
        Voltar ao início
      </button>
    </div>

    <!-- agradecimento -->
    <div v-else-if="enviada" class="av-card">
      <div class="av-check" aria-hidden="true">
        <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
          <path
            d="M20 6L9 17l-5-5"
            stroke="currentColor"
            stroke-width="2.5"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </div>
      <h1 class="av-title">Obrigado, {{ patientFirstName }}!</h1>
      <p class="av-sub">
        Sua avaliação ajuda outros pacientes a escolher com confiança.
      </p>

      <div class="av-retorno">
        <div class="avatar avatar--md avatar--ring">{{ doctorInitials }}</div>
        <div class="av-retorno__txt">
          <span class="av-retorno__q">Gostou do atendimento?</span>
          <span class="av-muted">
            Agende um retorno com {{ doctorShortName }}
          </span>
        </div>
      </div>

      <button class="btn btn--accent btn--block" @click="agendarRetorno">
        Agendar retorno
      </button>
      <button class="btn btn--outline btn--block" @click="voltarInicio">
        Voltar ao início
      </button>
    </div>

    <!-- formulário -->
    <div v-else class="av-card">
      <div class="avatar avatar--xl avatar--ring">{{ doctorInitials }}</div>

      <div>
        <h1 class="av-title">Como foi com {{ doctorShortName }}?</h1>
        <p class="av-sub">Sua opinião ajuda outros pacientes a escolher.</p>
      </div>

      <div
        class="av-stars"
        role="radiogroup"
        aria-label="Nota de 1 a 5 estrelas"
      >
        <button
          v-for="n in 5"
          :key="n"
          type="button"
          class="av-star av-star--btn"
          :class="{ 'av-star--on': n <= stars }"
          role="radio"
          :aria-checked="stars === n"
          :aria-label="`${n} ${n === 1 ? 'estrela' : 'estrelas'}`"
          @click="stars = n"
        >
          ★
        </button>
      </div>

      <div class="av-tags">
        <button
          v-for="t in tagsDisponiveis"
          :key="t.value"
          type="button"
          class="av-tag"
          :class="{ 'av-tag--on': tags.includes(t.value) }"
          :aria-pressed="tags.includes(t.value)"
          @click="toggleTag(t.value)"
        >
          {{ t.label }}
        </button>
      </div>

      <label class="av-field">
        <span class="av-muted">Conte como foi (opcional)</span>
        <textarea
          id="avaliacao-comentario"
          v-model="description"
          class="av-textarea"
          rows="3"
          maxlength="500"
          placeholder="O que você achou do atendimento?"
        />
      </label>

      <p v-if="erro" class="av-erro">{{ erro }}</p>

      <button
        class="btn btn--accent btn--block"
        :disabled="!stars || enviando"
        @click="enviar"
      >
        {{ enviando ? "Enviando…" : "Enviar avaliação" }}
      </button>
    </div>
  </div>
</template>

<script>
// Avaliação da consulta — funcionalidade 8 da Prova de Conceito do Edital
// INAS nº 01/2024, desenhada na etapa 15 da jornada (pós-consulta).
// Escala de 1 a 5 estrelas, etiquetas rápidas e comentário opcional; o envio
// desagua no estado de agradecimento com oferta de retorno.
import { Appointments } from "../services/appointments";

// Rótulos em substantivo, para não flexionar em gênero conforme o
// profissional avaliado. Os valores são o vocabulário aceito pela API.
const TAGS = [
  { value: "pontualidade", label: "Pontualidade" },
  { value: "atencao", label: "Atenção" },
  { value: "boa_escuta", label: "Boa escuta" }
];

export default {
  name: "AvaliarConsultaView",

  data() {
    return {
      loading: true,
      enviando: false,
      erro: null,
      stars: 0,
      tags: [],
      description: "",
      enviada: null,
      jaAvaliada: false,
      appointment: null,
      tagsDisponiveis: TAGS
    };
  },

  async mounted() {
    document.body.className = "";
    document.body.classList.add("Base");
    await this.carregar();
  },

  methods: {
    async carregar() {
      this.loading = true;
      try {
        const { data } = await Appointments.showPatientAppointment(
          this.appointmentId
        );
        this.appointment = data;
      } catch {
        this.erro = "Não foi possível carregar a consulta.";
      }

      // 404 aqui é o caso normal: consulta ainda não avaliada.
      try {
        const { data } = await Appointments.fetchTestimonial(
          this.appointmentId
        );
        if (data && data.id) {
          this.enviada = data;
          this.jaAvaliada = true;
        }
      } catch {
        this.jaAvaliada = false;
      }

      this.loading = false;
    },

    toggleTag(valor) {
      const i = this.tags.indexOf(valor);
      if (i >= 0) this.tags.splice(i, 1);
      else this.tags.push(valor);
    },

    tagLabel(valor) {
      const t = TAGS.find(x => x.value === valor);
      return t ? t.label : valor;
    },

    async enviar() {
      if (!this.stars) return;
      this.enviando = true;
      this.erro = null;
      try {
        const { data } = await Appointments.createTestimonial(
          this.appointmentId,
          {
            stars: this.stars,
            description: this.description || null,
            tags: this.tags
          }
        );
        this.enviada = data;
      } catch (e) {
        const detalhes =
          e && e.response && e.response.data && e.response.data.details;
        this.erro = detalhes
          ? detalhes.join(". ")
          : "Não foi possível enviar sua avaliação. Tente novamente.";
      } finally {
        this.enviando = false;
      }
    },

    agendarRetorno() {
      const doctorId = this.doctor && this.doctor.id;
      if (doctorId) {
        this.$router.push({
          name: "DoctorDetails",
          params: { doctor: doctorId }
        });
        return;
      }
      this.$router.push({ name: "Home" });
    },

    voltarInicio() {
      this.$router.push({ name: "Consultas" });
    }
  },

  computed: {
    appointmentId() {
      return this.$route.params.id;
    },

    doctor() {
      return this.appointment && this.appointment.doctor;
    },

    doctorShortName() {
      const nome = (this.doctor && this.doctor.name) || "";
      if (!nome) return "o profissional";
      return nome
        .split(" ")
        .slice(0, 2)
        .join(" ");
    },

    doctorInitials() {
      const nome = (this.doctor && this.doctor.name) || "";
      const iniciais = nome
        .split(" ")
        .filter(Boolean)
        .slice(0, 2)
        .map(p => p[0])
        .join("")
        .toUpperCase();
      return iniciais || "?";
    },

    patientFirstName() {
      const user = this.$store.state.user.currentUser || {};
      return (user.name || "").split(" ")[0] || "você";
    }
  }
};
</script>

<style lang="scss" scoped>
.av-wrap {
  display: flex;
  justify-content: center;
  padding: var(--space-8, 2rem) var(--space-4, 1rem) var(--space-12, 3rem);
}

.av-card {
  background: var(--color-bg-card, #fff);
  border: 1px solid var(--color-gray-200, #e2e8f0);
  border-radius: var(--radius-lg, 12px);
  box-shadow: var(--shadow-card, 0 2px 8px rgba(15, 23, 42, 0.06));
  padding: var(--space-8, 2rem);
  width: 100%;
  max-width: 560px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-5, 1.25rem);
  text-align: center;
}

.av-card--state {
  min-height: 180px;
  justify-content: center;
}

.av-title {
  font-size: var(--font-size-xl, 1.25rem);
  font-weight: var(--fw-bold, 700);
  color: var(--color-gray-900, #0f172a);
  margin: 0;
}

.av-sub {
  font-size: var(--font-size-sm, 0.875rem);
  color: var(--color-gray-500, #64748b);
  margin: var(--space-1, 0.25rem) 0 0;
}

.av-muted {
  font-size: var(--font-size-sm, 0.875rem);
  color: var(--color-gray-500, #64748b);
}

/* estrelas */
.av-stars {
  display: flex;
  gap: var(--space-2, 0.5rem);
}

.av-star {
  font-size: 2.25rem;
  line-height: 1;
  color: var(--color-gray-300, #cbd5e1);
}

.av-star--btn {
  background: none;
  border: 0;
  padding: 0 2px;
  cursor: pointer;
  transition: transform 0.12s ease, color 0.12s ease;
}

.av-star--btn:hover {
  transform: scale(1.12);
}

.av-star--on {
  color: #f59e0b;
}

.av-stars--readonly .av-star {
  font-size: 1.75rem;
}

/* etiquetas */
.av-tags {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2, 0.5rem);
  justify-content: center;
}

.av-tag {
  background: none;
  border: 1px solid var(--color-gray-300, #cbd5e1);
  border-radius: var(--radius-full, 9999px);
  padding: 6px 14px;
  font-size: var(--font-size-sm, 0.875rem);
  color: var(--color-gray-600, #475569);
  cursor: pointer;
  min-height: 36px;
}

.av-tag--on {
  border-color: var(--color-primary, #1a6cf7);
  background: var(--color-primary-light, #ebf2ff);
  color: var(--color-primary, #1a6cf7);
  font-weight: var(--fw-semibold, 600);
}

/* comentário */
.av-field {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: var(--space-2, 0.5rem);
  text-align: left;
}

.av-textarea {
  width: 100%;
  border: 1px solid var(--color-gray-300, #cbd5e1);
  border-radius: var(--radius-md, 8px);
  padding: var(--space-3, 0.75rem);
  font-size: var(--font-size-sm, 0.875rem);
  font-family: inherit;
  resize: vertical;
}

.av-textarea:focus {
  outline: 2px solid var(--color-primary, #1a6cf7);
  outline-offset: 1px;
  border-color: var(--color-primary, #1a6cf7);
}

.av-erro {
  color: var(--color-danger, #dc2626);
  font-size: var(--font-size-sm, 0.875rem);
  margin: 0;
}

.av-quote {
  font-size: var(--font-size-sm, 0.875rem);
  color: var(--color-gray-600, #475569);
  font-style: italic;
  margin: 0;
}

/* agradecimento */
.av-check {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: var(--color-success-light, #dcfce7);
  color: var(--color-success, #16a34a);
  display: flex;
  align-items: center;
  justify-content: center;
}

.av-retorno {
  display: flex;
  align-items: center;
  gap: var(--space-3, 0.75rem);
  width: 100%;
  padding: var(--space-4, 1rem);
  border: 1px solid var(--color-gray-200, #e2e8f0);
  border-radius: var(--radius-md, 8px);
  text-align: left;
}

.av-retorno__txt {
  display: flex;
  flex-direction: column;
}

.av-retorno__q {
  font-size: var(--font-size-sm, 0.875rem);
  font-weight: var(--fw-bold, 700);
  color: var(--color-gray-800, #1e293b);
}

.btn--block {
  width: 100%;
}

@media (max-width: 600px) {
  .av-wrap {
    padding: var(--space-4, 1rem) var(--space-3, 0.75rem) var(--space-8, 2rem);
  }

  .av-card {
    padding: var(--space-5, 1.25rem);
    border: 0;
    box-shadow: none;
  }

  .av-star {
    font-size: 2.5rem;
  }
}
</style>
